import * as THREE from 'three';
import { Enemy } from './Enemy';
import { EnemyType } from './EnemyType';

/**
 * Grunt enemy - basic melee attacker
 */
export class Grunt extends Enemy {
  private attackHitbox: THREE.Mesh;
  private attackAnimationProgress: number = 0;
  
  /**
   * Creates a new Grunt enemy
   * @param position The initial position
   * @param scene The scene to add the enemy to
   */
  constructor(position: THREE.Vector3, scene: THREE.Scene) {
    super(EnemyType.GRUNT, position, scene);
    
    // Create attack hitbox (invisible)
    const hitboxGeometry = new THREE.BoxGeometry(1.5, 1, 1.5);
    const hitboxMaterial = new THREE.MeshBasicMaterial({ 
      color: 0xff0000,
      transparent: true,
      opacity: 0.0, // Invisible
      depthWrite: false
    });
    this.attackHitbox = new THREE.Mesh(hitboxGeometry, hitboxMaterial);
    this.attackHitbox.position.z = 1.0; // In front of the enemy
    this.attackHitbox.visible = false;
    this.object.add(this.attackHitbox);
    
    // Add grunt-specific details to the model
    this.addModelDetails();
  }
  
  /**
   * Adds visual details to the grunt model
   */
  private addModelDetails(): void {
    // Add eyes
    const eyeGeometry = new THREE.SphereGeometry(0.1, 8, 8);
    const eyeMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
    
    const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
    leftEye.position.set(0.2, 0.2, 0.4);
    this.model.add(leftEye);
    
    const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
    rightEye.position.set(-0.2, 0.2, 0.4);
    this.model.add(rightEye);
    
    // Add pupils
    const pupilGeometry = new THREE.SphereGeometry(0.05, 8, 8);
    const pupilMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
    
    const leftPupil = new THREE.Mesh(pupilGeometry, pupilMaterial);
    leftPupil.position.set(0, 0, 0.05);
    leftEye.add(leftPupil);
    
    const rightPupil = new THREE.Mesh(pupilGeometry, pupilMaterial);
    rightPupil.position.set(0, 0, 0.05);
    rightEye.add(rightPupil);
    
    // Add weapon (club)
    const handleGeometry = new THREE.CylinderGeometry(0.05, 0.05, 0.6, 8);
    const handleMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
    const handle = new THREE.Mesh(handleGeometry, handleMaterial);
    
    const clubHeadGeometry = new THREE.SphereGeometry(0.15, 8, 8);
    const clubHeadMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
    const clubHead = new THREE.Mesh(clubHeadGeometry, clubHeadMaterial);
    clubHead.position.y = 0.3;
    
    const club = new THREE.Group();
    club.add(handle);
    club.add(clubHead);
    club.position.set(0.4, 0, 0.2);
    club.rotation.x = Math.PI / 4;
    this.model.add(club);
  }
  
  /**
   * Makes the grunt attack
   */
  public attack(): void {
    if (this.isDead || !this.isActive) return;
    
    const currentTime = performance.now() / 1000;
    if (currentTime - this.lastAttackTime < this.attackCooldown) {
      return; // Still on cooldown
    }
    
    super.attack();
    
    // Show attack hitbox
    this.attackHitbox.visible = true;
    
    // Reset attack animation progress
    this.attackAnimationProgress = 0;
  }
  
  /**
   * Updates the grunt
   * @param deltaTime Time since last update in seconds
   * @param playerPosition The current player position
   */
  public update(deltaTime: number, playerPosition: THREE.Vector3): void {
    super.update(deltaTime, playerPosition);
    
    // Update attack animation
    if (this.isAttacking) {
      this.updateAttackAnimation(deltaTime);
    }
  }
  
  /**
   * Updates the attack animation
   * @param deltaTime Time since last update in seconds
   */
  private updateAttackAnimation(deltaTime: number): void {
    // Progress the attack animation
    this.attackAnimationProgress += deltaTime;
    
    // Normalize progress to 0-1 range
    const normalizedProgress = Math.min(this.attackAnimationProgress / 1.0, 1.0);
    
    // Animate the model for attack
    if (normalizedProgress < 0.5) {
      // Wind up phase - pull back
      const windupProgress = normalizedProgress * 2; // 0 to 1
      this.model.rotation.x = -windupProgress * Math.PI / 4; // Lean back
    } else {
      // Strike phase - lunge forward
      const strikeProgress = (normalizedProgress - 0.5) * 2; // 0 to 1
      this.model.rotation.x = (1 - strikeProgress) * -Math.PI / 4 + strikeProgress * Math.PI / 6; // Lean forward
      
      // Check for hit at the peak of the strike
      if (strikeProgress > 0.5 && strikeProgress < 0.6) {
        this.checkAttackHit();
      }
    }
    
    // Reset model rotation when attack is complete
    if (normalizedProgress >= 1.0) {
      this.model.rotation.x = 0;
      this.attackHitbox.visible = false;
    }
  }
  
  /**
   * Checks if the attack hits the player
   */
  private checkAttackHit(): void {
    // This would normally use collision detection with the player
    // For now, we'll just log that an attack occurred
    console.log('Grunt attack!');
    
    // In a real implementation, this would check for collision with the player
    // and apply damage if a hit is detected
  }
  
  /**
   * Handles grunt death
   */
  protected die(): void {
    super.die();
    
    // Play death animation - fall over
    this.model.rotation.x = Math.PI / 2; // Fall forward
    this.model.position.z = this.getHeight() / 2; // Move forward as it falls
    this.model.position.y = 0; // Lie on the ground
  }
  
  /**
   * Disposes of resources
   */
  public dispose(): void {
    super.dispose();
    
    // Dispose of additional resources
    if (this.attackHitbox.geometry) {
      this.attackHitbox.geometry.dispose();
    }
    
    if (this.attackHitbox.material instanceof THREE.Material) {
      this.attackHitbox.material.dispose();
    }
  }
} 